import type { CaptureOptions, Destination, RoutingContext } from '../types';

const ALL_DESTINATIONS: readonly Destination[] = [
  'firstParty',
  'ga',
  'gtm',
  'posthog',
  'segment',
] as const;

/**
 * The routing policy: which destinations each side defaults to. Edit THIS
 * function when changing routing rules — it's deliberately separated from
 * `route` below so the override-merge mechanics can't be accidentally
 * dropped or skewed when policy changes.
 *
 * Current rule:
 * - GA receives every event.
 * - GTM (the dataLayer feed for ad/marketing tags) receives every event.
 *   Ad conversion tracking needs both sides — participant signups are the
 *   conversions the ad campaigns buy — and the adapter itself suppresses
 *   pushes when consent is denied.
 * - Segment receives every event. It's the warehouse pipe; we want both
 *   sides represented for downstream analysis.
 * - First-party receives every event, wherever Segment does. The two are
 *   deliberately kept in lockstep: during the migration window they
 *   dual-write, and comparing the two streams is how we verify the
 *   first-party collector before Segment is switched off. Any divergence
 *   in this policy would show up as a phantom gap in that comparison.
 * - PostHog receives events only when the user is on the researcher side.
 *   Participant and unknown-side events are excluded from PostHog as a
 *   cost-control measure. Exception: `AnalyticsClient.captureException`
 *   forces the PostHog destination on every side (via a per-call override)
 *   so crashes land in the error-tracking project regardless of side — error
 *   tracking is deliberately exempt from this cost-control rule.
 */
function defaultDestinationsFor(ctx: RoutingContext): Set<Destination> {
  const defaults = new Set<Destination>(['firstParty', 'ga', 'gtm', 'segment']);
  if (ctx.side === 'researcher') defaults.add('posthog');
  return defaults;
}

/**
 * Computes which destinations an event should be dispatched to.
 *
 * Two-step composition:
 * 1. `defaultDestinationsFor(ctx)` decides the policy defaults for this side.
 * 2. Per-event overrides merge with those defaults — `{ posthog: true }`
 *    forces PostHog for one specific event even if defaults exclude it;
 *    `{ ga: false }` suppresses GA for one. Omitted destinations inherit
 *    the default.
 *
 * Editing this function should be rare — it's just the merge. Policy
 * changes belong in `defaultDestinationsFor`.
 */
export function route(
  options: CaptureOptions | undefined,
  ctx: RoutingContext,
): Destination[] {
  const defaults = defaultDestinationsFor(ctx);
  const overrides = options?.destinations ?? {};
  return ALL_DESTINATIONS.filter(
    (destination) => overrides[destination] ?? defaults.has(destination),
  );
}
